import type { DraftOp } from '@/api/types'
import type { Column, Groups, Relation, Table } from '@/types'

/**
 * Conjunto de entidades que o USUÁRIO LOCAL alterou desde o último flush.
 *
 * `diffToOps` compara o espelho do servidor com o estado local inteiro, então
 * sozinho ele expressa "deixe o servidor igual à minha tela" — e apaga o que
 * outro usuário criou enquanto esta aba estava desatualizada. Filtrar as ops
 * por este conjunto restringe cada save ao que realmente partiu daqui.
 *
 * Marcar demais é inofensivo (o diff simplesmente não gera op quando o conteúdo
 * é igual); marcar de menos perde a edição do usuário. Por isso a detecção usa
 * identidade de objeto, que nunca deixa passar uma alteração real.
 */
let touchedKeys = new Set<string>()

export interface TouchedDiagramState {
  tables: Table[]
  rels: Relation[]
  groups: Groups
  defaults: unknown
  projectName: string
  dialect: string
}

export function tableKey(tableId: string): string {
  return `table:${tableId}`
}

export function columnKey(tableId: string, columnId: string): string {
  return `col:${tableId}:${columnId}`
}

function columnOrderKey(tableId: string): string {
  return `cols:${tableId}`
}

function indexKey(tableId: string, indexId: string): string {
  return `idx:${tableId}:${indexId}`
}

function indexOrderKey(tableId: string): string {
  return `idxs:${tableId}`
}

/**
 * Chave de escopo de uma op. `null` = op não endereçada a uma entidade
 * concreta (project/settings e ops desconhecidas), que passam sem filtro para
 * preservar o comportamento anterior.
 */
export function opTouchKey(op: DraftOp): string | null {
  switch (op.op) {
    case 'table.upsert':
    case 'table.remove':
    case 'table.move':
      return op.id ? tableKey(op.id) : null
    case 'tableOrder.set':
      return 'tableOrder'
    case 'column.upsert':
    case 'column.remove':
      return op.tableId && op.id ? columnKey(op.tableId, op.id) : null
    case 'columnOrder.set':
      return op.tableId ? columnOrderKey(op.tableId) : null
    case 'index.upsert':
    case 'index.remove':
      return op.tableId && op.id ? indexKey(op.tableId, op.id) : null
    case 'indexOrder.set':
      return op.tableId ? indexOrderKey(op.tableId) : null
    case 'rel.upsert':
    case 'rel.remove':
      return op.id ? `rel:${op.id}` : null
    case 'relOrder.set':
      return 'relOrder'
    case 'group.upsert':
    case 'group.remove':
      return op.id ? `group:${op.id}` : null
    case 'groupOrder.set':
      return 'groupOrder'
    default:
      return null
  }
}

export function filterOpsByTouched(ops: DraftOp[], touched: Set<string>): DraftOp[] {
  return ops.filter((op) => {
    const key = opTouchKey(op)
    return key === null || touched.has(key)
  })
}

export function markTouched(key: string): void {
  touchedKeys.add(key)
}

export function markTouchedColumns(tableId: string, columnIds: Iterable<string>): void {
  touchedKeys.add(tableKey(tableId))
  touchedKeys.add(columnOrderKey(tableId))
  for (const columnId of columnIds) touchedKeys.add(columnKey(tableId, columnId))
}

/** Devolve as chaves acumuladas e zera o conjunto (o chamador assume o flush). */
export function takeTouchedKeys(): Set<string> {
  const taken = touchedKeys
  touchedKeys = new Set<string>()
  return taken
}

/** Devolve as chaves ao conjunto quando o flush falha, para o retry reemiti-las. */
export function restoreTouchedKeys(keys: Iterable<string>): void {
  for (const key of keys) touchedKeys.add(key)
}

export function resetTouchedKeys(): void {
  touchedKeys = new Set<string>()
}

export function peekTouchedKeys(): ReadonlySet<string> {
  return touchedKeys
}

/**
 * Registra o que mudou entre dois estados LOCAIS consecutivos do diagrama.
 * Só deve ser chamado para transições originadas nesta aba — cargas remotas
 * (`loadDiagram`) rodam sob `autosaveSuppressed` e nunca chegam aqui.
 */
export function markLocalDiagramChange(prev: TouchedDiagramState, next: TouchedDiagramState): void {
  if (prev.tables !== next.tables) markTableChanges(prev.tables, next.tables)
  if (prev.rels !== next.rels) markCollectionChanges(prev.rels, next.rels, 'rel', 'relOrder')
  if (prev.groups !== next.groups) markGroupChanges(prev.groups, next.groups)
}

function markTableChanges(prevTables: Table[], nextTables: Table[]): void {
  const prevById = new Map(prevTables.map((table) => [table.id, table]))
  const nextById = new Map(nextTables.map((table) => [table.id, table]))

  for (const [id, prevTable] of prevById) {
    const nextTable = nextById.get(id)
    if (!nextTable) {
      touchedKeys.add(tableKey(id))
      for (const column of prevTable.cols) touchedKeys.add(columnKey(id, column.id))
      continue
    }
    if (prevTable === nextTable) continue
    touchedKeys.add(tableKey(id))
    markColumnChanges(id, prevTable.cols, nextTable.cols)
    markIndexChanges(id, prevTable.indexes ?? [], nextTable.indexes ?? [])
  }

  for (const [id, nextTable] of nextById) {
    if (prevById.has(id)) continue
    touchedKeys.add(tableKey(id))
    touchedKeys.add(columnOrderKey(id))
    touchedKeys.add(indexOrderKey(id))
    for (const column of nextTable.cols) touchedKeys.add(columnKey(id, column.id))
    for (const index of nextTable.indexes ?? []) touchedKeys.add(indexKey(id, index.id))
  }

  if (!sameIdOrder(prevTables, nextTables)) touchedKeys.add('tableOrder')
}

function markColumnChanges(tableId: string, prevCols: Column[], nextCols: Column[]): void {
  const prevById = new Map(prevCols.map((column) => [column.id, column]))
  const nextById = new Map(nextCols.map((column) => [column.id, column]))

  for (const [id, prevColumn] of prevById) {
    const nextColumn = nextById.get(id)
    if (!nextColumn || nextColumn !== prevColumn) touchedKeys.add(columnKey(tableId, id))
  }
  for (const [id] of nextById) {
    if (!prevById.has(id)) touchedKeys.add(columnKey(tableId, id))
  }
  if (!sameIdOrder(prevCols, nextCols)) touchedKeys.add(columnOrderKey(tableId))
}

function markIndexChanges(
  tableId: string,
  prevIndexes: Array<{ id: string }>,
  nextIndexes: Array<{ id: string }>,
): void {
  const prevById = new Map(prevIndexes.map((index) => [index.id, index]))
  const nextById = new Map(nextIndexes.map((index) => [index.id, index]))

  for (const [id, prevIndex] of prevById) {
    const nextIndex = nextById.get(id)
    if (!nextIndex || nextIndex !== prevIndex) touchedKeys.add(indexKey(tableId, id))
  }
  for (const [id] of nextById) {
    if (!prevById.has(id)) touchedKeys.add(indexKey(tableId, id))
  }
  if (!sameIdOrder(prevIndexes, nextIndexes)) touchedKeys.add(indexOrderKey(tableId))
}

function markCollectionChanges(
  prevItems: Array<{ id: string }>,
  nextItems: Array<{ id: string }>,
  prefix: string,
  orderKey: string,
): void {
  const prevById = new Map(prevItems.map((item) => [item.id, item]))
  const nextById = new Map(nextItems.map((item) => [item.id, item]))

  for (const [id, prevItem] of prevById) {
    const nextItem = nextById.get(id)
    if (!nextItem || nextItem !== prevItem) touchedKeys.add(`${prefix}:${id}`)
  }
  for (const [id] of nextById) {
    if (!prevById.has(id)) touchedKeys.add(`${prefix}:${id}`)
  }
  if (!sameIdOrder(prevItems, nextItems)) touchedKeys.add(orderKey)
}

function markGroupChanges(prevGroups: Groups, nextGroups: Groups): void {
  const prevIds = Object.keys(prevGroups)
  const nextIds = Object.keys(nextGroups)
  for (const id of new Set([...prevIds, ...nextIds])) {
    if (prevGroups[id] !== nextGroups[id]) touchedKeys.add(`group:${id}`)
  }
  const sameOrder =
    prevIds.length === nextIds.length && prevIds.every((id, index) => id === nextIds[index])
  if (!sameOrder) touchedKeys.add('groupOrder')
}

function sameIdOrder(prev: Array<{ id: string }>, next: Array<{ id: string }>): boolean {
  if (prev.length !== next.length) return false
  for (let index = 0; index < prev.length; index += 1) {
    if (prev[index].id !== next[index].id) return false
  }
  return true
}
